import { useLocation } from "@tanstack/react-router"
import { motion } from "framer-motion"
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useCTAGlow } from "./hooks/useCTAGlow"
import { useCTAScrollEffects } from "./hooks/useCTAScrollEffects"
import { useCTAVisibility } from "./hooks/useCTAVisibility"
import { brackyAnimations } from "./LandingBrackyAvatar"

interface LandingCTAProps {
  variant?: "inline" | "tall-screen" | "mobile" | undefined
  externalVisibility?: boolean | undefined
  label?: string | undefined
}

export function LandingCTA({ variant = "inline", externalVisibility, label }: LandingCTAProps) {
  const location = useLocation()
  const [isPressStarted, setIsPressStarted] = useState(false)
  const [hasNavigated, setHasNavigated] = useState(false)

  // Detect device type for responsive behavior
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== "undefined" ? window.innerWidth < 640 : false)
  const [_isDesktop, setIsDesktop] = useState<boolean>(typeof window !== "undefined" ? window.innerWidth >= 640 : true)

  // Update device state on resize
  useEffect(() => {
    if (typeof window === "undefined") return

    const handleResize = () => {
      const width = window.innerWidth
      setIsMobile(width < 640)
      setIsDesktop(width >= 640)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Get visibility state based on variant
  const visibility = useCTAVisibility(variant)
  const scrollEffects = useCTAScrollEffects(variant, externalVisibility)
  const glowCalculations = useCTAGlow(isMobile, variant)

  const isLandingPage =
    typeof window !== "undefined" && (window.location.pathname === "/" || window.location.pathname === "/x")

  const handleGetStarted = () => {
    if (hasNavigated) return
    setHasNavigated(true)
    window.open("https://bracky.app", "_blank")
  }

  const handleMouseDown = () => {
    setIsPressStarted(true)
    window.dispatchEvent(new Event("landingCTApressStart"))
    brackyAnimations.playDoubleNod()
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    if (isPressStarted) {
      const rect = e.currentTarget.getBoundingClientRect()
      const x = e.clientX
      const y = e.clientY

      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        handleGetStarted()
      }
    }
    setIsPressStarted(false)
    window.dispatchEvent(new Event("landingCTApressEnd"))
  }

  const handleMouseLeave = () => {
    setIsPressStarted(false)
    window.dispatchEvent(new Event("landingCTApressEnd"))
  }

  const handleTouchStart = () => {
    setIsPressStarted(true)
    window.dispatchEvent(new Event("landingCTApressStart"))
    brackyAnimations.playDoubleNod()
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isPressStarted) {
      const rect = e.currentTarget.getBoundingClientRect()
      const touch = e.changedTouches[0]
      if (!touch) return
      const x = touch.clientX
      const y = touch.clientY

      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        handleGetStarted()
      }
    }
    setIsPressStarted(false)
    window.dispatchEvent(new Event("landingCTApressEnd"))
  }

  // Determine visibility based on variant
  let shouldHideCTA = false

  if (variant === "inline") {
    shouldHideCTA =
      isLandingPage &&
      visibility.isBracketSectionInView &&
      visibility.isExpandedCardHalfVisible &&
      visibility.isUserRested &&
      visibility.allowBracketInteractions
  } else if (variant === "mobile") {
    // Mobile CTA hides when user has interacted with demo and is still viewing it
    shouldHideCTA = visibility.demoInteracted && visibility.isDemoSectionInView
  }

  // Calculate visibility for tall-screen variant
  const tallScreenVisible =
    variant === "tall-screen"
      ? externalVisibility !== undefined
        ? externalVisibility
        : scrollEffects.ctaVisible
      : true

  // Determine button label
  const getButtonLabel = () => {
    if (label) return label

    if (variant === "mobile") {
      const isXRoute = location.pathname === "/x"
      const baseLabel = isXRoute ? "Continue Betting Live" : "Start Betting Live"
      return scrollEffects.backgroundProgress < 0.02 ? baseLabel : "Bet Live Now"
    }

    return "Start Betting With Bracky"
  }

  const buttonLabel = getButtonLabel()
  const isStartTradingLabel =
    variant === "mobile" && (buttonLabel === "Start Betting Live" || buttonLabel === "Start Betting Live")

  // Interpolate background color for mobile variant
  const interpolateColor = (progress: number) => {
    const blueR = 0
    const blueG = 28
    const blueB = 255
    const blueA = 1
    const darkR = 4
    const darkG = 5
    const darkB = 14
    const darkA = 0.75

    const r = Math.round(blueR + (darkR - blueR) * progress)
    const g = Math.round(blueG + (darkG - blueG) * progress)
    const b = Math.round(blueB + (darkB - blueB) * progress)
    const a = blueA + (darkA - blueA) * progress

    return `rgba(${r}, ${g}, ${b}, ${a})`
  }

  const backgroundColor = variant === "mobile" ? interpolateColor(scrollEffects.backgroundProgress) : "#001CFF"

  // Desktop inline and tall-screen render - ensure mobile is completely excluded
  if (!isMobile && (variant === "inline" || variant === "tall-screen")) {
    const containerClass =
      variant === "tall-screen"
        ? "flex flex-col items-center justify-center transition-opacity duration-300 ease-out"
        : "hidden sm:flex justify-center mb-16"

    return (
      <div
        className={containerClass}
        style={
          variant === "tall-screen"
            ? { opacity: tallScreenVisible ? 1 : 0 }
            : {
                opacity: shouldHideCTA ? 0 : 1,
                visibility: shouldHideCTA ? "hidden" : "visible",
                pointerEvents: shouldHideCTA ? "none" : "auto",
              }
        }
      >
        <motion.button
          type="button"
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className="relative inline-flex items-center bg-[#001CFF] text-white font-semibold px-30 py-[.9rem] rounded-full transition-all whitespace-nowrap w-[90%] sm:w-[80%] md:w-md sm:min-w-104 justify-center text-[1.18rem] sm:text-[1.109rem] md:text-[1.18rem]"
          style={{
            boxShadow: glowCalculations.haloBoxShadow,
          }}
          transition={{ duration: 0.01 }}
          whileHover={{
            scale: 1.01,
            boxShadow: glowCalculations.hoverHaloBoxShadow,
            backgroundColor: "rgba(0, 28, 255, 1)",
            transition: { duration: 0.04 },
          }}
          whileTap={{
            scale: 0.99,
            boxShadow: glowCalculations.activeHaloBoxShadow,
            backgroundColor: "rgba(0, 28, 255, 1)",
            transition: { duration: 0.01 },
          }}
        >
          <span className="relative z-10 flex items-center">
            {buttonLabel}
            <motion.span
              animate={{ x: [0, 2, 0] }}
              transition={{ duration: 1.2, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
              className="ml-[0.2rem]"
            >
              →
            </motion.span>
          </span>
        </motion.button>
      </div>
    )
  }

  // Mobile render with portal - only when explicitly variant === "mobile"
  if (variant === "mobile" && isMobile && !shouldHideCTA) {
    const content = (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.0, duration: 0.3 }}
        className="fixed bottom-[2.95rem] left-0 right-0 z-520 flex justify-center pointer-events-none sm:hidden"
      >
        <div className="flex flex-col items-center w-full">
          <motion.button
            type="button"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            className={`relative pointer-events-auto backdrop-blur-lg text-white ${isStartTradingLabel ? "font-semibold" : "font-bold"} w-[90%] max-w-[420px] h-[3.22rem] flex items-center justify-center text-[1.0325rem] md:text-lg rounded-[3.1rem] transition-all duration-100`}
            style={{
              boxShadow: isPressStarted ? glowCalculations.activeHaloBoxShadow : glowCalculations.haloBoxShadow,
              backgroundColor,
              touchAction: "manipulation",
              transform: isPressStarted ? "scale(0.95)" : "scale(1)",
            }}
          >
            <span className="relative z-10 translate-y-[-0.05rem] tracking-[-.005rem] flex items-center">
              {buttonLabel}
              <motion.span
                animate={{ x: [0, 2, 0] }}
                transition={{ duration: 1.2, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
                className="ml-[0.2rem]"
              >
                →
              </motion.span>
            </span>
          </motion.button>
          <motion.p
            className={`${variant === "mobile" ? "" : "hidden sm:block"} mt-2 text-white/70 text-[0.775rem] leading-snug text-center pointer-events-none w-[87%] max-w-[420px]`}
            style={{ opacity: scrollEffects.microOpacity }}
            transition={{ duration: 0.15 }}
          >
            Sign in with X—Start betting live in seconds
          </motion.p>
        </div>
      </motion.div>
    )

    return typeof window !== "undefined" ? createPortal(content, document.body) : null
  }

  return null
}
